/**
 * Attribution Ledger Drawer — Right-side panel with Stripe-level ledger UI.
 * Clean rows, strong typography, lots of white space, zero gridlines.
 */
import { useState, useMemo } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ChevronDown, ChevronRight } from "lucide-react";
import type { FinancialAttribution } from "@/data/fie/attributionTypes";
import { getAttributionBreakdown, type AttributionBreakdown } from "@/data/fie/attributionTypes";
import { cn } from "@/lib/utils";

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(value);
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attributions: FinancialAttribution[];
  title?: string;
  subtitle?: string;
}

export function AttributionLedgerDrawer({ open, onOpenChange, attributions, title, subtitle }: Props) {
  const [activeTab, setActiveTab] = useState<"summary" | "by-job" | "by-source" | "ledger">("ledger");
  const breakdown = getAttributionBreakdown(attributions);

  const narrative = useMemo(() => buildNarrative(attributions, breakdown, title), [attributions, breakdown, title]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[480px] sm:w-[540px] p-0 border-l border-border/40 bg-card overflow-y-auto" side="right">
        {/* Header */}
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border/30">
          <SheetTitle className="text-lg font-bold tracking-tight">{title || "Attribution Ledger"}</SheetTitle>
          {subtitle && <p className="text-[13px] text-muted-foreground -mt-1">{subtitle}</p>}

          {/* Summary strip */}
          <div className="flex items-center gap-6 mt-3 pt-3 border-t border-border/20">
            <div>
              <p className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">Total</p>
              <p className="text-xl font-bold tracking-tight">{formatCurrency(breakdown.total)}</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <div className="h-2 w-2 rounded-full bg-success/60" />
                <span className="text-xs text-muted-foreground">Actual</span>
                <span className="text-xs font-medium">{formatCurrency(breakdown.totalActual)}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="h-2 w-2 rounded-full bg-warning/60" />
                <span className="text-xs text-muted-foreground">Estimated</span>
                <span className="text-xs font-medium">{formatCurrency(breakdown.totalEstimated)}</span>
              </div>
            </div>
          </div>
        </SheetHeader>

        {/* Narrative Summary */}
        {narrative && (
          <div className="px-6 py-3 border-b border-border/20 bg-muted/20">
            <p className="text-[13px] leading-relaxed text-muted-foreground">{narrative}</p>
          </div>
        )}

        {/* Tab bar — clean underline style */}
        <div className="px-6 border-b border-border/30">
          <div className="flex gap-0">
            {(["summary", "by-job", "by-source", "ledger"] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={cn(
                  "px-3 py-3 text-[12px] font-medium tracking-wide transition-colors relative",
                  activeTab === tab
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground/70"
                )}
              >
                {tab === "by-job" ? "By Job" : tab === "by-source" ? "By Source" : tab.charAt(0).toUpperCase() + tab.slice(1)}
                {activeTab === tab && (
                  <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-foreground rounded-full" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {activeTab === "ledger" && <LedgerTab attributions={attributions} />}
          {activeTab === "summary" && <SummaryTab breakdown={breakdown} />}
          {activeTab === "by-job" && <ByJobTab breakdown={breakdown} />}
          {activeTab === "by-source" && <BySourceTab breakdown={breakdown} />}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ── Ledger Tab — THE WOW ──
function LedgerTab({ attributions }: { attributions: FinancialAttribution[] }) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Sort by amount descending so top contributors are first
  const sorted = useMemo(() =>
    [...attributions].sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount)),
    [attributions]
  );

  return (
    <div className="space-y-0.5">
      {sorted.map((a, idx) => (
        <LedgerRow
          key={a.id}
          attribution={a}
          expanded={expandedId === a.id}
          onToggle={() => setExpandedId(expandedId === a.id ? null : a.id)}
          isTopContributor={idx < 3}
        />
      ))}
      <div className="pt-4 mt-4 border-t border-border/20">
        <p className="text-[11px] text-muted-foreground">
          {attributions.length} records · {attributions.filter(a => a.isActual).length} actual · {attributions.filter(a => !a.isActual).length} estimated
        </p>
      </div>
    </div>
  );
}

function LedgerRow({ attribution: a, expanded, onToggle, isTopContributor = false }: {
  attribution: FinancialAttribution; expanded: boolean; onToggle: () => void; isTopContributor?: boolean;
}) {
  return (
    <div>
      <button
        onClick={onToggle}
        className={cn(
          "w-full flex items-center gap-3 py-3 px-2 -mx-2 rounded-lg transition-all duration-150",
          "hover:bg-muted/40",
          expanded && "bg-muted/30",
          !isTopContributor && "opacity-75"
        )}
      >
        {/* Status dot */}
        <div className={cn(
          "h-2 w-2 rounded-full shrink-0",
          a.isActual ? "bg-success/60" : "bg-warning/60"
        )} />

        {/* Left: category + description */}
        <div className="flex-1 text-left min-w-0">
          <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground/60 leading-none mb-1">
            {a.category}
          </p>
          <p className={cn(
            "truncate leading-tight",
            isTopContributor ? "text-[14px] font-semibold text-foreground" : "text-[13px] font-medium text-foreground"
          )}>
            {a.description}
          </p>
        </div>

        {/* Right: amount */}
        <div className="text-right shrink-0">
          <p className={cn(
            "font-semibold tabular-nums",
            isTopContributor ? "text-[15px] text-foreground" : "text-[14px]",
            a.isActual ? "text-foreground" : "text-muted-foreground"
          )}>
            {formatCurrency(a.amount)}
          </p>
        </div>

        {/* Source ID */}
        <span className="text-[10px] font-mono text-muted-foreground/40 w-16 text-right shrink-0 truncate">
          {a.sourceId}
        </span>

        {/* Chevron */}
        {expanded
          ? <ChevronDown className="h-3 w-3 text-muted-foreground/40 shrink-0" />
          : <ChevronRight className="h-3 w-3 text-muted-foreground/40 shrink-0" />
        }
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="ml-7 pl-4 border-l-2 border-border/20 pb-3 mb-1 animate-in slide-in-from-top-1 duration-150">
          <div className="space-y-2 text-xs">
            <Row label="Source Type" value={a.sourceType} />
            <Row label="Source Table" value={a.sourceTable} />
            <Row label="Status" value={a.isActual ? "Actual — verified data" : "Estimated — derived from assumptions"} />
            {a.jobId && <Row label="Job" value={a.jobId} mono />}
            <Row label="Created" value={new Date(a.createdAt).toLocaleString()} />
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn("font-medium text-foreground", mono && "font-mono text-[11px]")}>{value}</span>
    </div>
  );
}

// ── Summary Tab ──
function SummaryTab({ breakdown }: { breakdown: ReturnType<typeof getAttributionBreakdown> }) {
  return (
    <div className="space-y-4">
      {Object.entries(breakdown.byCategory).map(([cat, attrs]) => {
        const total = attrs.reduce((s, a) => s + a.amount, 0);
        const actualPct = attrs.length > 0 ? Math.round((attrs.filter(a => a.isActual).length / attrs.length) * 100) : 0;
        return (
          <div key={cat} className="flex items-center justify-between py-2">
            <div>
              <p className="text-[13px] font-medium capitalize">{cat}</p>
              <p className="text-[11px] text-muted-foreground">{attrs.length} records · {actualPct}% actual</p>
            </div>
            <p className="text-[14px] font-semibold tabular-nums">{formatCurrency(total)}</p>
          </div>
        );
      })}
    </div>
  );
}

// ── By Job Tab ──
function ByJobTab({ breakdown }: { breakdown: ReturnType<typeof getAttributionBreakdown> }) {
  return (
    <div className="space-y-3">
      {Object.entries(breakdown.byJob).map(([jobId, attrs]) => {
        const total = attrs.reduce((s, a) => s + a.amount, 0);
        const cats = [...new Set(attrs.map(a => a.category))];
        return (
          <div key={jobId} className="flex items-center justify-between py-2">
            <div>
              <p className="text-[13px] font-mono font-medium">{jobId === "_no_job" ? "No Job" : jobId}</p>
              <p className="text-[11px] text-muted-foreground capitalize">{cats.join(", ")}</p>
            </div>
            <p className="text-[14px] font-semibold tabular-nums">{formatCurrency(total)}</p>
          </div>
        );
      })}
    </div>
  );
}

// ── By Source Tab ──
function BySourceTab({ breakdown }: { breakdown: ReturnType<typeof getAttributionBreakdown> }) {
  return (
    <div className="space-y-3">
      {Object.entries(breakdown.bySourceType).map(([st, attrs]) => {
        const total = attrs.reduce((s, a) => s + a.amount, 0);
        return (
          <div key={st} className="flex items-center justify-between py-2">
            <div>
              <p className="text-[13px] font-medium capitalize">{st}</p>
              <p className="text-[11px] text-muted-foreground">{attrs.length} records</p>
            </div>
            <p className="text-[14px] font-semibold tabular-nums">{formatCurrency(total)}</p>
          </div>
        );
      })}
    </div>
  );
}

// ── Narrative Builder ──
function buildNarrative(
  attributions: FinancialAttribution[],
  breakdown: AttributionBreakdown,
  title?: string
): string | null {
  if (!attributions.length) return null;

  const total = breakdown.total;
  const actualCount = attributions.filter(a => a.isActual).length;
  const estimatedCount = attributions.length - actualCount;
  const actualPct = Math.round((actualCount / attributions.length) * 100);
  const estimatedPct = 100 - actualPct;

  const jobCount = Object.keys(breakdown.byJob).filter(k => k !== "_no_job").length;
  const categoryCount = Object.keys(breakdown.byCategory).length;

  const label = title || "this value";
  const parts: string[] = [];

  // Main sentence
  const jobPhrase = jobCount > 0 ? ` across ${jobCount} job${jobCount > 1 ? "s" : ""}` : "";
  parts.push(`This ${formatCurrency(total)} in ${label} is composed of ${attributions.length} records${jobPhrase}, spanning ${categoryCount} categor${categoryCount > 1 ? "ies" : "y"}.`);

  // Actuals split
  if (estimatedCount > 0 && actualCount > 0) {
    parts.push(`${actualPct}% is backed by verified actuals and ${estimatedPct}% remains estimated.`);
  } else if (estimatedCount === 0) {
    parts.push("All values are backed by verified actuals.");
  } else {
    parts.push("All values are currently estimated.");
  }

  return parts.join(" ");
}
